import React, { useState, Suspense, useMemo, useRef, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { IconButton, Paper, Tab, Tabs, Tooltip, Typography, withStyles } from "@material-ui/core";
import { TabContainer, TabPanel } from "../../sc-order/Order/Details";
import {
    BackButton,
    copyToClipBoard,
    hasAtLeastOnePermission,
    hasPermission,
    parseURL
} from "../../sc-utilities";
import config from "../../../config/config";
import { download } from "../../sc-utilities/download.utils";
import { snackActions } from "../../sc-utilities/SnackBar";
import { CloudDownload, FindInPage, Link as LinkIcon } from "@material-ui/icons";
import { informationColors } from "../../../themes";
import {NavLink, Redirect} from "react-router-dom";
import { updateURL } from "../../sc-utilities";
import * as actions from "./Single/store/erasureReportActions";
import { singleTableColumns } from "./Single/columns/columns";
import { collectionTableColumns } from "./Collection/columns/columns";
import { formatProductTypes } from '../../sc-admin/SystemCertificate/utils';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        margin: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
});

export const actionNavLink = (tooltipTitle, icon, rowData, type, activeCustomer, activeCustomerName, permissions) => {
    const iconButton = <IconButton component='div'>
        {icon}
    </IconButton>

    const activeCustomerParams = `&ActiveCustomerID=${activeCustomer}&ActiveCustomerName=${activeCustomerName}`;

    let productType;
    let detailsPermission;
    switch (type) {
        case 'MobileProduct':
            productType = "mobile-product";
            detailsPermission = 'product_list';
            break;
        case 'PCProduct':
            productType = "pc-product";
            detailsPermission = 'product_list';
            break;
        case 'StorageProduct':
            productType = "storage-product";
            detailsPermission = 'product_list';
            break;
        case 'WatchProduct':
            productType = "watch-product";
            detailsPermission = 'product_list';
            break;
        case 'HeadphoneProduct':
            productType = "headphone-product";
            detailsPermission = 'product_list';
            break;
        default:
            break;
    }
    const detailsLink = `/${productType}/details?productID=${rowData.ProductID}&type=${type}${activeCustomerParams}`;

    return (
        <Tooltip title={tooltipTitle}>
            <NavLink to={hasPermission(permissions, detailsPermission) ? detailsLink : "/reports/erasure-reports"}>
                {iconButton}
            </NavLink>
        </Tooltip>
    )
}

const ReportList = React.lazy(() => import('./Single/SinglePDFReportList'));
const ReportRegenerate = React.lazy(() => import('./Single/SinglePDFReportRegenerate'));
const CollectionList = React.lazy(() => import('./Collection/CollectionReportList'));
const ReportFinder = React.lazy(() => import('../../sc-admin/ReportFinder'));
const ErasureReportTabs = (props) => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions, activeCustomer, onRegenerateReport, theme, onFetchProductType, activeCustomerName } = props;

    const defaultTabValue = Number(window.location.hash.slice(1)) || 0;
    const [tabValue, setTabValue] = useState(defaultTabValue);
    useEffect(() => setTabValue(defaultTabValue), [defaultTabValue]);

    const [redirectOptions, ] = useState({ redirect: false, erasureReportID: null, redirectPath: '', downloading: true });
    const [regenerateAction, setRegenerateAction] = useState(0);
    const [regenerateModalState, setRegenerateModalState] = useState({ open: false, erasureReportId: null });

    const [productTypes, setProductTypes] = useState(null);
    const [loading, setLoading] = useState({productTypes: false});

    let visible = columns && columns['ErasureReport'];

    const handleCloseSafe = () => {
        setRegenerateModalState({ open: false, erasureReportId: null });
    };

    const handleChange = (event, value) => {
        updateURL(value);
        setTabValue(value);
    };

    const handleCloseDanger = useCallback(async (erasureReportId = null) => {
        let result = await onRegenerateReport({ ReportID: erasureReportId ? erasureReportId : regenerateModalState.erasureReportId, ActiveCustomerID: activeCustomer });
        setRegenerateModalState({ open: false, erasureReportId: null });

        if (result.data) {
            setRegenerateAction(regenerateAction + 1);
        }
    }, [regenerateAction, onRegenerateReport, activeCustomer, setRegenerateModalState, setRegenerateAction, regenerateModalState.erasureReportId]);

    useEffect(() => {
        const fetchProductTypes = async () => {
            setLoading(prevState => {return {...prevState, productTypes: true}});
            let productTypesList = await onFetchProductType({ ActiveCustomerID: activeCustomer });
            let list = [];
            let formattedProductTypes = productTypesList.reduce((obj, item) => Object.assign(obj, { [item.key]: formatProductTypes(item.label, translations) }), {});
            if (formattedProductTypes) {
                for (const [key] of Object.entries(formattedProductTypes)) {
                    list.push(key);
                }
            }
            setProductTypes({ lookup: formattedProductTypes, defaultFilter: list });
            setLoading(prevState => {return {...prevState, productTypes: false}});
        }
        if (!productTypes) {
            fetchProductTypes();
        }

        return () => { };
        // eslint-disable-next-line
    }, [productTypes, translations]);

    const singleColumns = useMemo(() => {

        const handleDownload = async erasureReportID => {
            const objectURL = `${config.backend_domain}:${config.backend_port}/report/download?ReportID=${erasureReportID}&ActiveCustomerID=${activeCustomer}`;
            let exportDataSuccessful = await download(objectURL);
            tableRef.current && tableRef.current.onQueryChange();
            if (!exportDataSuccessful) {
                return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
            }
        };

        const createSingleAction = (rowData, translations) => {
            return <div>
                {hasPermission(permissions, 'erasure_report_download') && rowData.GeneratedStatus === 2 ?
                    <Tooltip title={translations["download_report"] || "Download report(s)"}>
                        <IconButton component='div' aria-label={translations["download_report"] || "Download report(s)"}
                                    onClick={() => handleDownload(rowData.ErasureReportID)}>
                            {<CloudDownload />}
                        </IconButton>
                    </Tooltip>
                    : null
                }
                {
                    actionNavLink(translations["product_details"] || "Product details", <FindInPage />, rowData, rowData.Product?.Type, activeCustomer, activeCustomerName, permissions)
                }
                {
                    rowData?.Shared ?
                        <Tooltip title={translations["share_report"] || "Share report"}>
                            <IconButton component='div' aria-label={translations["share_report"] || "Share report"}
                                onClick={() => {
                                    const details = JSON.parse(rowData?.Shared?.Details);
                                    copyToClipBoard(`${config.web}/shared?ID=${encodeURIComponent(rowData?.Shared?.Hash)}&ProductID=${details?.ProductID}&ProductType=${details?.ProductType}`)
                                    return snackActions.snackBarComponent(translations['copied_to_clipboard'] || `Copied to clipboard `, 'success');
                                }}>
                                {<LinkIcon />}
                            </IconButton>
                        </Tooltip> : null
                }

            </div>
        };

        return singleTableColumns(productTypes, translations, visible, createSingleAction, CustomChip);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translations, visible, permissions, activeCustomer, productTypes, activeCustomerName, loading.productTypes]);

    const collectionColumns = useMemo(() => {
        const queryParams = parseURL(window.location.href.split("#")[0]);

        const handleDownload = async erasureReportID => {
            const objectURL = `${config.backend_domain}:${config.backend_port}/report/download?ReportID=${erasureReportID}&ActiveCustomerID=${activeCustomer}`;
            let exportDataSuccessful = await download(objectURL);
            tableRef.current && tableRef.current.onQueryChange();
            if (!exportDataSuccessful) {
                return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
            }
        }

        const actionNavLink = (tooltipTitle, icon, rowData, type) => {
            const iconButton = <IconButton component='div'>
                {icon}
            </IconButton>

            let erasureReportType;
            let detailsPermission;
            let idName;
            let collectionType;
            switch (type) {
                case 'colli_collection':
                    erasureReportType = "container";
                    detailsPermission = 'colli_list';
                    idName = "ColliInventoryID";
                    collectionType = "Colli";
                    break;
                case 'transport_colli_collection':
                    erasureReportType = "transport-container";
                    detailsPermission = 'transport_colli_list';
                    idName = "TransportColliInventoryID";
                    collectionType = "TransportColli";
                    break;
                case 'order_collection':
                    erasureReportType = "orders";
                    detailsPermission = 'order_list';
                    idName = "OrderInventoryID";
                    collectionType = "Order";
                    break;
                default:
                    break;
            }

            let detailsLink = "/reports/erasure-reports";
            if (rowData && rowData[collectionType]) {
                detailsLink = `/logistic/${erasureReportType}?${idName}=${rowData[collectionType][idName]}`;
            }

            return (
                <Tooltip title={tooltipTitle}>
                    <NavLink to={hasPermission(permissions, detailsPermission) ? detailsLink : "/reports/erasure-reports"}>
                        {iconButton}
                    </NavLink>
                </Tooltip>
            )
        }

        const createCollectionAction = (rowData, translations) => {
            return <div>
                {hasPermission(permissions, 'erasure_report_download') && rowData.GeneratedStatus === 2 ?
                    <Tooltip title={translations["download_report"] || "Download report(s)"}>
                        <IconButton component='div' aria-label={translations["download_report"] || "Download report(s)"}
                                    onClick={() => handleDownload(rowData.ErasureReportID)}>
                            {<CloudDownload />}
                        </IconButton>
                    </Tooltip>
                    : null
                }
                {rowData.ErasureReportType !== "custom_collection" &&
                    actionNavLink(translations["details"] || "Details", <FindInPage />, rowData, rowData.ErasureReportType)}
            </div>
        };
        return collectionTableColumns(translations, visible, createCollectionAction, CustomChip, queryParams);
    }, [translations, visible, permissions, activeCustomer]);


    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    if (!hasAtLeastOnePermission(permissions, [
        "erasure_report_download",
        'product_load'
    ])) {
        singleTableColumns.shift();
    }

    return (
        <div className={classes.root}>
            <Paper>
                {BackButton(translations)}
                <Tabs
                    value={tabValue}
                    onChange={handleChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                >
                    {hasPermission(permissions, 'erasure_report_list') ?
                        <Tab label={translations['single_pdf_report'] || "Single PDF Report"} value={0} /> : null}
                    {hasPermission(permissions, 'collection_reports_view') ?
                        <Tab label={translations['collection_reports'] || "Collection Reports"} value={1} /> : null}
                    {hasPermission(permissions, 'report_finder_view') ?
                        <Tab label={translations['report_finder'] || "Report Finder"} value={2} /> : null}
                </Tabs>
            </Paper>
            {hasPermission(permissions, 'erasure_report_list') ?
                <TabPanel index={0} value={tabValue}>
                    <TabContainer dir={theme.direction}>
                        <Suspense fallback={<div>Loading...</div>}>
                            <section>
                                {hasPermission(permissions, 'erasure_report_list') ?
                                    <ReportList
                                        regenerateAction={regenerateAction}
                                        tableColumns={singleColumns}
                                        tableRef={tableRef}
                                        loading={loading}
                                    /> : null}
                                {hasPermission(permissions, 'erasure_report_regenerate') ? (
                                    <ReportRegenerate
                                        entityName={translations['erasure_report'] || 'Erasure Report'}
                                        handleCloseDanger={handleCloseDanger}
                                        handleCloseSafe={handleCloseSafe}
                                        regenerateModalState={regenerateModalState}
                                    />
                                ) : null}
                            </section>
                        </Suspense>
                    </TabContainer>
                </TabPanel> : null}
            {hasPermission(permissions, 'collection_reports_view') ?
                <TabPanel index={1} value={tabValue}>
                    <TabContainer>
                        <Suspense fallback={<div>Loading...</div>}>
                            <section>
                                {hasPermission(permissions, 'collection_reports_view') ?
                                    <CollectionList regenerateAction={regenerateAction}
                                        tableColumns={collectionColumns}
                                        tableRef={tableRef}
                                        permissions={permissions} />
                                    : null}
                            </section>
                        </Suspense>
                    </TabContainer>
                </TabPanel> : null}
            {
                hasPermission(permissions, 'report_finder_view') ? <TabPanel index={2} value={tabValue}>
                    <TabContainer>
                        <Suspense fallback={<div>Loading...</div>}>
                            <section>
                                <ReportFinder></ReportFinder>
                            </section>
                        </Suspense>
                    </TabContainer>
                </TabPanel> : null
            }
        </div>
    )
}

export const CustomChip = props => {
    const { keyValue, value, className } = props;
    let possibleStyles = {
        chipFailed: {
            color: informationColors.error.dark,
            fontSize: 14,
            display: 'inline-block'

        },
        chipReady: {
            color: informationColors.warning.dark,
            fontSize: 14,
            display: 'inline-block'

        },
        chipInProgress:
        {
            color: informationColors.info.dark,
            fontSize: 14,
            display: 'inline-block'

        },
        chipGenerated: {
            color: informationColors.success.dark,
            fontSize: 14,
            display: 'inline-block'

        }
    }
    return (
        <Typography key={keyValue} className={className} style={possibleStyles[className]}>{value === 'n/a' ? value : capitalize(value)}</Typography>
    );
};

const capitalize = (s) => {
    if (typeof s !== 'string') return ''
    return s.charAt(0).toUpperCase() + s.slice(1)
}

const mapStateToProps = state => {
    return {
        activeCustomerName: state?.core?.login?.activeCustomer?.Name ?? null,
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};
const mapDispatchToProps = dispatch => {
    return {
        onRegenerateReport: data => dispatch(actions.regenerateErasureReport(data)),
        onFetchProductType: params => dispatch(actions.listProductTypes(params)),
    };
}
export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(ErasureReportTabs));